import { ReactNode, useEffect } from "react";
import { useNavigation as useFunnelNavigation } from "../../../hooks/useNavigation";
import { analyticsGA4 } from "../../../tracking/analytics";
import { getDeviceInfo, getPageInfo } from "./track/utils";
import { useUser } from "../auth";
import { useAppSettingsSnapshot } from "../../settings";
import { useBrandSnapshot } from "../branding";
import { getFunnelReferrer } from "../../../utils/referrer";
import { usePreEligibilityDataSnapshot } from "../../funnels/pre-eligibility/state/preEligibilityState";
import { format } from "date-fns";
import { sessionTime } from "../../../tracking/utils/sessionTime";
import { getUtmParams } from "../../../tracking/getUtmParams";
import hashing from "tracking/utils/hashing";

export const PreEligibilityPageViewEventTracker = ({ children }: { children: ReactNode }) => {
  const { lastLocation, currentLocation } = useFunnelNavigation();
  const { profile, isLoading } = useUser();
  const { currency, locale } = useAppSettingsSnapshot();
  const { claimDetails, flightDetails, itineraryFlights } = usePreEligibilityDataSnapshot();
  const brandSnapshot = useBrandSnapshot();
  const utmParams = getUtmParams();
  const disruptedFlight =
    itineraryFlights?.find((flight) => flight.disrupted) || itineraryFlights[0];
  const disruptedAirline = disruptedFlight?.airline?.uniqueIdentifier;
  const referrer = getFunnelReferrer();

  useEffect(() => {
    if (isLoading) return;

    analyticsGA4.track("page_view", {
      brand: brandSnapshot?.name || "",
      process: "Claim",
      ...getPageInfo(lastLocation?.pathname),
      ...getDeviceInfo(),
      claim_referrer: referrer,
      ota_brand: "",
      transaction_source: utmParams?.source,
      funnel_type: "defaultFlow",
      total_time_claim: sessionTime.getDuration(),
      business_type: brandSnapshot?.name ? "B2B" : "B2C",
      page_language: locale,
      page_currency: currency,
      is_logged: !!profile,
      user_id: profile?.userProfileUuid || "",
      hashed_email_id: profile?.email && hashing.sha256(profile?.email),
      user_type: "Ineligible",
      user_city: "",
      hashed_user_city: "",
      user_postal_code: "",
      hashed_user_postal_code: "",
      user_country: "",
      hashed_user_country: profile?.countryCode && hashing.sha256(profile?.countryCode),
      hashed_user_phone_number:
        profile?.phoneNumber?.number && hashing.sha256(profile?.phoneNumber?.number),
      hashed_user_first_name: profile?.firstName && hashing.sha256(profile?.firstName),
      hashed_user_last_name: profile?.lastName && hashing.sha256(profile?.lastName),
      claim_id: "",
      regulation: claimDetails?.potentialRegulation || "",
      disruption_type: claimDetails?.claimType || "",
      claim_type: claimDetails?.claimType || "",
      delayed_flight_disruption_reason: claimDetails.airlineReason || "",
      denied_boarding_reservation_surrendered: claimDetails.voluntarilySurrendered ? "Y" : "N",
      denied_boarding_total_delay: claimDetails.totalDelay || "",
      fellow_passengers: 0,
      flight_type: flightDetails.anyConnections ? "connection" : "direct",
      is_on_hold: 0,
      cancelled_flight_disruption_reason:
        claimDetails.claimType === "cancelled" ? claimDetails.airlineReason : "",
      cancelled_flight_total_delay:
        claimDetails.claimType === "cancelled" ? claimDetails.totalDelay : "",
      delayed_flight_total_delay:
        claimDetails.claimType === "delayed" ? claimDetails.totalDelay : "",
      claim_revenue: 0,
      disrupted_airline: disruptedAirline || "",
      disruption_segment_destination: disruptedFlight?.departureAirport?.iata || "",
      disruption_segment_source: disruptedFlight?.arrivalAirport?.iata || "",
      trip_date: disruptedFlight?.flightDate
        ? format(disruptedFlight?.flightDate, "dd-MM-yyyy")
        : "",
      trip_departure: disruptedFlight?.departureAirport?.iata || "",
      trip_destination: disruptedFlight?.arrivalAirport?.iata || "",
      service_fee: 0,
      total_compensation: 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation, lastLocation, isLoading]);

  return <>{children}</>;
};
